import React, { useEffect, useMemo, useState } from "react";
import { apiUrl } from "../../config/api";
import WebsiteInquiries from "../website-inquiries/WebsiteInquiries";
import "./OlivaMarketing.css";

function headers() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function initialDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return {
    dateFrom: start.toISOString().slice(0, 10),
    dateTo: end.toISOString().slice(0, 10),
  };
}

const EMPTY_INTEGRATION = {
  isEnabled: false,
  appId: "",
  accessToken: "",
  adAccountId: "",
  businessId: "",
  pageId: "",
  instagramAccountId: "",
  whatsappPhoneNumberId: "",
  messagingEnabled: false,
  appSecret: "",
  appSecretConfigured: false,
  webhookVerifyToken: "",
  webhookVerifyTokenConfigured: false,
  graphApiVersion: "v25.0",
  accessTokenConfigured: false,
  pixelId: "",
  conversionsApiEnabled: false,
  browserPixelEnabled: false,
  conversionsAccessToken: "",
  conversionsAccessTokenConfigured: false,
};

function currency(value) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function number(value) {
  return new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(Number(value || 0));
}

function OlivaMarketing() {
  const [tab, setTab] = useState("dashboard");
  const [dates, setDates] = useState(initialDates);
  const [integration, setIntegration] = useState(EMPTY_INTEGRATION);
  const [draft, setDraft] = useState(EMPTY_INTEGRATION);
  const [insights, setInsights] = useState({ rows: [], totals: {} });
  const [leadSummary, setLeadSummary] = useState({});
  const [firstParty, setFirstParty] = useState({ rows: [], totals: {} });
  const [capabilities, setCapabilities] = useState({ canConfigure: false, canManageContacts: false });
  const [configOpen, setConfigOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [testEventCode, setTestEventCode] = useState("");
  const [messageCampaignFilter, setMessageCampaignFilter] = useState("");

  const ready = integration.isEnabled && integration.accessTokenConfigured && integration.adAccountId;
  const totalRoas = useMemo(() => {
    const spend = Number(insights.totals?.spend || 0);
    return spend ? Number(firstParty.totals?.confirmedRevenue || 0) / spend : 0;
  }, [insights, firstParty]);
  const campaignRows = useMemo(() => {
    const byId = new Map();
    (insights.rows || []).forEach((row) => byId.set(row.campaignId, { ...row }));
    (firstParty.rows || []).forEach((row) => {
      const current = byId.get(row.campaignId) || {
        campaignId: row.campaignId,
        campaignName: row.campaignName,
        spend: 0,
        reach: 0,
        impressions: 0,
        clicks: 0,
        ctr: 0,
      };
      byId.set(row.campaignId, { ...current, ovm: row });
    });
    return [...byId.values()].sort((a, b) => Number(b.spend || 0) - Number(a.spend || 0));
  }, [insights, firstParty]);

  async function loadIntegration() {
    const response = await fetch(apiUrl("/oliva/marketing/integrations/meta"), { headers: headers() });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || "No se pudo leer la configuración.");
    setIntegration(data.integration);
    setCapabilities(data.capabilities || {});
    setDraft({
      ...EMPTY_INTEGRATION,
      ...data.integration,
      accessToken: "",
      conversionsAccessToken: "",
    });
    return data.integration;
  }

  async function loadDashboard(currentIntegration = integration) {
    const params = new URLSearchParams(dates).toString();
    const requests = [
      fetch(apiUrl(`/oliva/marketing/lead-summary?${params}`), { headers: headers() }),
      fetch(apiUrl(`/oliva/marketing/first-party-insights?${params}`), { headers: headers() }),
    ];
    if (currentIntegration.isEnabled && currentIntegration.accessTokenConfigured) {
      requests.push(fetch(apiUrl(`/oliva/marketing/meta/insights?${params}`), { headers: headers() }));
    }
    const responses = await Promise.all(requests);
    const leadData = await responses[0].json();
    if (!leadData.success) throw new Error(leadData.message || "No se pudieron leer los contactos.");
    setLeadSummary(leadData.summary || {});
    const firstPartyData = await responses[1].json();
    if (!firstPartyData.success) throw new Error(firstPartyData.message || "No se pudo medir el embudo manual.");
    setFirstParty(firstPartyData);
    if (responses[2]) {
      const insightData = await responses[2].json();
      if (!insightData.success) throw new Error(insightData.message || "No se pudo leer Meta Ads.");
      setInsights(insightData);
    } else {
      setInsights({ rows: [], totals: {} });
    }
  }

  async function refresh() {
    setLoading(true);
    setMessage("");
    try {
      const current = await loadIntegration();
      await loadDashboard(current);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // The initial range is intentionally loaded once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveIntegration(event) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl("/oliva/marketing/integrations/meta"), {
        method: "PUT",
        headers: headers(),
        body: JSON.stringify(draft),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "No se pudo guardar Meta Ads.");
      const conversionsResponse = await fetch(apiUrl("/oliva/marketing/integrations/meta/conversions"), {
        method: "PUT",
        headers: headers(),
        body: JSON.stringify(draft),
      });
      const conversionsData = await conversionsResponse.json();
      if (!conversionsData.success) throw new Error(conversionsData.message || "No se pudo guardar Conversions API.");
      setIntegration(conversionsData.integration);
      setDraft((current) => ({
        ...current,
        ...conversionsData.integration,
        accessToken: "",
        conversionsAccessToken: "",
        appSecret: "",
        webhookVerifyToken: "",
      }));
      setConfigOpen(false);
      setMessage("Configuración de Meta Ads guardada.");
      await loadDashboard(data.integration);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function testConversions() {
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl("/oliva/marketing/integrations/meta/conversions/test"), {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ ...draft, testEventCode }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Meta rechazó el evento de prueba.");
      setMessage(`${data.message} Recibidos: ${data.eventsReceived}.`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function applyFilters() {
    setLoading(true);
    setMessage("");
    try {
      await loadDashboard();
    } catch (error) {
      setInsights({ rows: [], totals: {} });
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleMessageChanged() {
    try {
      await loadDashboard();
    } catch (error) {
      setMessage(error.message);
    }
  }

  function openCampaignMessages(campaignId) {
    setMessageCampaignFilter(campaignId);
    setTab("messages");
  }

  async function testIntegration() {
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(apiUrl("/oliva/marketing/integrations/meta/test"), {
        method: "POST",
        headers: headers(),
        body: JSON.stringify(draft),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "Meta rechazó la conexión.");
      setMessage(`${data.message} ${data.account?.name || ""}`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="oliva-marketing">
      <header className="oliva-marketing__header">
        <div>
          <span>Oliva y Miel</span>
          <h1>Marketing y mensajes</h1>
          <p>Campañas, formularios recibidos, conversaciones y ventas vinculadas manualmente.</p>
        </div>
        {capabilities.canConfigure ? <button type="button" className="oliva-marketing__config" onClick={() => setConfigOpen(true)}>
          <span className="material-symbols-outlined">settings</span>
          Configurar Meta
        </button> : null}
      </header>

      <nav className="oliva-marketing__tabs">
        <button type="button" className={tab === "dashboard" ? "is-active" : ""} onClick={() => setTab("dashboard")}>
          <span className="material-symbols-outlined">monitoring</span>Campañas
        </button>
        {capabilities.canManageContacts ? <button type="button" className={tab === "messages" ? "is-active" : ""} onClick={() => setTab("messages")}>
          <span className="material-symbols-outlined">inbox</span>Mensajes
          {Number(leadSummary.unread || 0) > 0 ? <b>{leadSummary.unread}</b> : null}
        </button> : null}
      </nav>

      {message ? <div className="oliva-marketing__message">{message}</div> : null}

      {tab === "messages" ? (
        <>
          {messageCampaignFilter ? (
            <div className="oliva-marketing__message-filter">
              <span>Mostrando mensajes de {messageCampaignFilter === "unattributed" ? "origen sin atribución" : `campaña ${messageCampaignFilter}`}</span>
              <button type="button" onClick={() => setMessageCampaignFilter("")}>Ver todos</button>
            </div>
          ) : null}
          <WebsiteInquiries campaignId={messageCampaignFilter} onChanged={handleMessageChanged} />
        </>
      ) : (
        <main className="oliva-marketing__dashboard">
          <section className="oliva-marketing__filters">
            <label>Desde<input type="date" value={dates.dateFrom} onChange={(event) => setDates((value) => ({ ...value, dateFrom: event.target.value }))} /></label>
            <label>Hasta<input type="date" value={dates.dateTo} onChange={(event) => setDates((value) => ({ ...value, dateTo: event.target.value }))} /></label>
            <button type="button" onClick={applyFilters} disabled={loading}>
              <span className="material-symbols-outlined">refresh</span>Actualizar
            </button>
          </section>

          <section className="oliva-marketing__cards">
            <article><span>Inversión Meta</span><strong>{currency(insights.totals?.spend)}</strong></article>
            <article><span>Mensajes/formularios OVM</span><strong>{number(firstParty.totals?.leads)}</strong><small>{number(firstParty.totals?.responded)} respondidos · {number(firstParty.totals?.attributedLeads)} atribuidos</small></article>
            <article><span>Ventas confirmadas manualmente</span><strong>{number(firstParty.totals?.confirmedSales)}</strong></article>
            <article><span>Costo por contacto atribuido</span><strong>{currency(Number(firstParty.totals?.attributedLeads || 0) > 0 ? Number(insights.totals?.spend || 0) / Number(firstParty.totals.attributedLeads) : 0)}</strong></article>
            <article><span>Venta confirmada OVM</span><strong>{currency(firstParty.totals?.confirmedRevenue)}</strong></article>
            <article><span>ROAS OVM / gasto Meta</span><strong>{number(totalRoas)}x</strong></article>
          </section>

          {!ready ? (
            <section className="oliva-marketing__empty">
              <span className="material-symbols-outlined">campaign</span>
              <h2>Conecta la cuenta publicitaria de Oliva y Miel</h2>
              <p>Los contactos ya aparecen aquí. Configura Meta para sumar inversión y resultados de campañas.</p>
              {capabilities.canConfigure ? <button type="button" onClick={() => setConfigOpen(true)}>Configurar Meta Ads</button> : null}
            </section>
          ) : (
            <section className="oliva-marketing__table-card">
              <div><h2>Campañas de Meta</h2><span>{integration.adAccountId}</span></div>
              <div className="oliva-marketing__table-wrap">
                <table>
                  <thead><tr><th>Campaña</th><th>Inversión Meta</th><th>Alcance</th><th>Impresiones</th><th>Clicks</th><th>CTR</th><th>Contactos OVM</th><th>Ventas manuales</th><th>ROAS OVM</th></tr></thead>
                  <tbody>
                    {campaignRows.map((row) => (
                      <tr key={row.campaignId}>
                        <td>
                          <strong>{row.campaignName}</strong><small>{row.campaignId}</small>
                          {capabilities.canManageContacts ? <button type="button" className="oliva-marketing__message-link" onClick={() => openCampaignMessages(row.campaignId)}>Ver mensajes</button> : null}
                        </td>
                        <td>{currency(row.spend)}</td><td>{number(row.reach)}</td><td>{number(row.impressions)}</td>
                        <td>{number(row.clicks)}</td><td>{number(row.ctr)}%</td><td>{number(row.ovm?.leads)}<small>{number(row.ovm?.responded)} respondidos</small></td>
                        <td>{number(row.ovm?.confirmedSales)}</td><td>{number(row.spend ? Number(row.ovm?.confirmedRevenue || 0) / row.spend : 0)}x</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!insights.rows.length && !loading ? <p>No hay campañas con actividad en este rango.</p> : null}
              </div>
            </section>
          )}
        </main>
      )}

      {configOpen && capabilities.canConfigure ? (
        <div className="oliva-marketing__backdrop" onMouseDown={() => setConfigOpen(false)}>
          <form className="oliva-marketing__modal" onSubmit={saveIntegration} onMouseDown={(event) => event.stopPropagation()}>
            <div><h2>Meta Ads · Oliva y Miel</h2><button type="button" onClick={() => setConfigOpen(false)}>×</button></div>
            <label className="oliva-marketing__switch"><input type="checkbox" checked={draft.isEnabled} onChange={(event) => setDraft((value) => ({ ...value, isEnabled: event.target.checked }))} />Activar lectura de campañas</label>
            <label>App ID<input value={draft.appId} onChange={(event) => setDraft((value) => ({ ...value, appId: event.target.value }))} /></label>
            <label>Access Token<input type="password" value={draft.accessToken} placeholder={draft.accessTokenConfigured ? "Configurado; déjalo vacío para conservarlo" : ""} onChange={(event) => setDraft((value) => ({ ...value, accessToken: event.target.value }))} /></label>
            <label>Ad Account ID<input value={draft.adAccountId} placeholder="act_123456789" onChange={(event) => setDraft((value) => ({ ...value, adAccountId: event.target.value }))} /></label>
            <label>Business ID <small>(opcional)</small><input value={draft.businessId} onChange={(event) => setDraft((value) => ({ ...value, businessId: event.target.value }))} /></label>
            <label>Graph API<input value={draft.graphApiVersion} onChange={(event) => setDraft((value) => ({ ...value, graphApiVersion: event.target.value }))} /></label>
            <hr />
            <label className="oliva-marketing__switch"><input type="checkbox" checked={draft.messagingEnabled} onChange={(event) => setDraft((value) => ({ ...value, messagingEnabled: event.target.checked }))} />Activar mensajes de Instagram y WhatsApp</label>
            <label>Facebook Page ID<input value={draft.pageId} onChange={(event) => setDraft((value) => ({ ...value, pageId: event.target.value }))} /></label>
            <label>Instagram Account ID<input value={draft.instagramAccountId} onChange={(event) => setDraft((value) => ({ ...value, instagramAccountId: event.target.value }))} /></label>
            <label>WhatsApp Phone Number ID<input value={draft.whatsappPhoneNumberId} onChange={(event) => setDraft((value) => ({ ...value, whatsappPhoneNumberId: event.target.value }))} /></label>
            <label>Meta App Secret<input type="password" value={draft.appSecret} placeholder={draft.appSecretConfigured ? "Configurado; déjalo vacío para conservarlo" : ""} onChange={(event) => setDraft((value) => ({ ...value, appSecret: event.target.value }))} /></label>
            <label>Webhook Verify Token<input type="password" value={draft.webhookVerifyToken} placeholder={draft.webhookVerifyTokenConfigured ? "Configurado; déjalo vacío para conservarlo" : ""} onChange={(event) => setDraft((value) => ({ ...value, webhookVerifyToken: event.target.value }))} /><small>Callback: {window.location.origin.replace(/:\\d+$/, ":8080")}/oliva/meta-messages/webhook</small></label>
            <hr />
            <label className="oliva-marketing__switch"><input type="checkbox" checked={Boolean(draft.browserPixelEnabled)} onChange={(event) => setDraft((value) => ({ ...value, browserPixelEnabled: event.target.checked }))} />Activar Meta Pixel en las páginas de eventos y cotizaciones</label>
            <label className="oliva-marketing__switch"><input type="checkbox" checked={draft.conversionsApiEnabled} onChange={(event) => setDraft((value) => ({ ...value, conversionsApiEnabled: event.target.checked }))} />Activar Conversions API</label>
            <label>Pixel/Dataset ID<input value={draft.pixelId} onChange={(event) => setDraft((value) => ({ ...value, pixelId: event.target.value }))} /></label>
            <label>Token de Conversions API<input type="password" value={draft.conversionsAccessToken} placeholder={draft.conversionsAccessTokenConfigured ? "Configurado; déjalo vacío para conservarlo" : ""} onChange={(event) => setDraft((value) => ({ ...value, conversionsAccessToken: event.target.value }))} /></label>
            <label>Código Test Events<input value={testEventCode} onChange={(event) => setTestEventCode(event.target.value)} /><small>No se guarda.</small></label>
            <footer><button type="button" onClick={testIntegration} disabled={busy}>Probar cuenta</button><button type="button" onClick={testConversions} disabled={busy || !testEventCode}>Probar evento</button><button type="submit" disabled={busy}>{busy ? "Guardando..." : "Guardar"}</button></footer>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default OlivaMarketing;
